import React, { useCallback, useEffect, useState } from "react";
import { MousePointerClick, Plus, Pencil, History } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select";
import CatalogTable from "../components/CatalogTable";
import { CatalogLoadingCard, CatalogErrorCard } from "../components/CatalogStateCard";
import EntityFormDialog from "../components/EntityFormDialog";
import StatusBadge from "../components/StatusBadge";
import StatusSelectField, { SIX_STATE_STATUS_VALUES } from "../components/StatusSelectField";
import { useEmailCatalogEntityList } from "../../../hooks/useEmailCatalogEntityList";
import { createCta, createCtaVersion, getCtas, getOffers, getPsAssets, updateCtaMetadata } from "../../../lib/emailMarketingApi";

/**
 * CtasPanel — EMAILS-IMPL-009
 *
 * CTA is the one non-Image family that does NOT fit AssetFamilyPanel's
 * single-content-field shape: it also carries the CTA Fingerprint
 * (CTA-SPEC-001 §11) -- five fields (Primary Action, Commitment Level,
 * Customer Journey Stage, Compatible Offer, Compatible P.S.) that
 * "permanently document the action identity of the CTA," independent of
 * Creative implementation.
 *
 * All five are genuinely operator-enterable per governance's own worked
 * example (Primary Action "Get Estimate", Commitment Level "Low",
 * Customer Journey Stage "Quote Start", Compatible Offer "OFF0001",
 * Compatible P.S. "PS0003") -- none of them are implementation/governance
 * metadata unsuitable for direct entry, so none are hidden or fabricated
 * here. Primary Action / Commitment Level / Customer Journey Stage remain
 * plain text (no governance/model enum constrains them -- confirmed
 * directly against CtaFingerprint, email_marketing_assets.py: all five
 * fields are bare Optional[str]).
 *
 * EMAILS-IMPL-009A: Compatible Offer / Compatible P.S. are now governed
 * Selects, backed by the real getOffers()/getPsAssets() catalog reads
 * (previously free-text, per EMAILS-IMPL-009's own disclosed gap -- no
 * `emails`-gated list endpoint existed yet). The stored value is exactly
 * the governed ID string (e.g. "OFF0001") the Select option carries --
 * identical in shape to what the free-text field already sent, so this is
 * NOT a backend/CTA-fingerprint contract change: CtaFingerprint.
 * compatible_offer/compatible_ps are both documented directly on the
 * model as "governed OFF####/PS#### reference" (a bare string), never a
 * human label or a different value. Options display "governed ID — name"
 * so an operator can tell same-prefix Assets apart; only the ID is
 * persisted.
 *
 * This form/response NEVER includes a Broadcast tracking marker
 * (cid=/cr=/br=) -- those are Broadcast execution identifiers generated at
 * send time (a later checkpoint), not part of the CTA Asset's own
 * identity, and nothing in this component or emailMarketingApi.js
 * generates one.
 */
const STATUS_VALUES = SIX_STATE_STATUS_VALUES;

function emptyFingerprint() {
  return { primaryAction: "", commitmentLevel: "", customerJourneyStage: "", compatibleOffer: "", compatiblePs: "" };
}

function fingerprintPayload(fp) {
  const trimmed = {
    primary_action: fp.primaryAction.trim() || null,
    commitment_level: fp.commitmentLevel.trim() || null,
    customer_journey_stage: fp.customerJourneyStage.trim() || null,
    compatible_offer: fp.compatibleOffer.trim() || null,
    compatible_ps: fp.compatiblePs.trim() || null,
  };
  const hasAny = Object.values(trimmed).some((v) => v !== null);
  return hasAny ? trimmed : null;
}

function fingerprintFromResponse(cta_fingerprint) {
  if (!cta_fingerprint) return emptyFingerprint();
  return {
    primaryAction: cta_fingerprint.primary_action || "",
    commitmentLevel: cta_fingerprint.commitment_level || "",
    customerJourneyStage: cta_fingerprint.customer_journey_stage || "",
    compatibleOffer: cta_fingerprint.compatible_offer || "",
    compatiblePs: cta_fingerprint.compatible_ps || "",
  };
}

const NO_COMPATIBLE_ASSET = "__none__";

// EMAILS-IMPL-009A: fetched once per CtasPanel mount (see the hook below),
// not per FingerprintFields render -- both the create AND new-Version
// forms share the SAME two option lists.
function useCompatibleAssetOptions() {
  const [offers, setOffers] = useState([]);
  const [psAssets, setPsAssets] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | unavailable

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [offerRows, psRows] = await Promise.all([getOffers(), getPsAssets()]);
        if (!cancelled) {
          setOffers(offerRows || []);
          setPsAssets(psRows || []);
          setStatus("ready");
        }
      } catch {
        if (!cancelled) setStatus("unavailable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { offers, psAssets, status };
}

function FingerprintFields({ value, onChange, testPrefix, offers, psAssets, optionsStatus }) {
  return (
    <div className="space-y-3 rounded-md border border-white/10 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">CTA Fingerprint (optional)</div>
      {optionsStatus === "unavailable" && (
        <p className="text-xs text-amber-300" data-testid={`${testPrefix}-compatible-assets-unavailable`}>
          Unable to load Offers/P.S. for selection right now -- Compatible Offer/P.S. will be left unset.
        </p>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${testPrefix}-primary-action`}>Primary Action</Label>
          <Input
            id={`${testPrefix}-primary-action`}
            value={value.primaryAction}
            onChange={(e) => onChange({ ...value, primaryAction: e.target.value })}
            placeholder="e.g. Get Estimate"
            data-testid={`${testPrefix}-primary-action-input`}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${testPrefix}-commitment-level`}>Commitment Level</Label>
          <Input
            id={`${testPrefix}-commitment-level`}
            value={value.commitmentLevel}
            onChange={(e) => onChange({ ...value, commitmentLevel: e.target.value })}
            placeholder="e.g. Low"
            data-testid={`${testPrefix}-commitment-level-input`}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${testPrefix}-journey-stage`}>Customer Journey Stage</Label>
          <Input
            id={`${testPrefix}-journey-stage`}
            value={value.customerJourneyStage}
            onChange={(e) => onChange({ ...value, customerJourneyStage: e.target.value })}
            placeholder="e.g. Quote Start"
            data-testid={`${testPrefix}-journey-stage-input`}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${testPrefix}-compatible-offer`}>Compatible Offer</Label>
          <Select
            value={value.compatibleOffer || NO_COMPATIBLE_ASSET}
            onValueChange={(v) => onChange({ ...value, compatibleOffer: v === NO_COMPATIBLE_ASSET ? "" : v })}
            disabled={optionsStatus !== "ready"}
          >
            <SelectTrigger id={`${testPrefix}-compatible-offer`} data-testid={`${testPrefix}-compatible-offer-select`}>
              <SelectValue placeholder={optionsStatus === "loading" ? "Loading…" : "None"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_COMPATIBLE_ASSET} data-testid={`${testPrefix}-compatible-offer-option-none`}>None</SelectItem>
              {offers.map((o) => (
                <SelectItem key={o.offer_id} value={o.offer_id} data-testid={`${testPrefix}-compatible-offer-option-${o.offer_id}`}>
                  {o.offer_id} — {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${testPrefix}-compatible-ps`}>Compatible P.S.</Label>
          <Select
            value={value.compatiblePs || NO_COMPATIBLE_ASSET}
            onValueChange={(v) => onChange({ ...value, compatiblePs: v === NO_COMPATIBLE_ASSET ? "" : v })}
            disabled={optionsStatus !== "ready"}
          >
            <SelectTrigger id={`${testPrefix}-compatible-ps`} data-testid={`${testPrefix}-compatible-ps-select`}>
              <SelectValue placeholder={optionsStatus === "loading" ? "Loading…" : "None"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_COMPATIBLE_ASSET} data-testid={`${testPrefix}-compatible-ps-option-none`}>None</SelectItem>
              {psAssets.map((p) => (
                <SelectItem key={p.ps_id} value={p.ps_id} data-testid={`${testPrefix}-compatible-ps-option-${p.ps_id}`}>
                  {p.ps_id} — {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export default function CtasPanel() {
  // See AssetFamilyPanel.jsx's own comment: these must be stable
  // identities (useCallback, not fresh inline closures) or `refresh`
  // retriggers its own effect every render.
  //
  // EMAILS-IMPL-009A: `list` now calls the real persistent catalog
  // (getCtas()) instead of EMAILS-IMPL-009's `async () => []` stub.
  const listFn = useCallback(() => getCtas(), []);
  const createFn = useCallback(
    ({ name, status, canonicalCta, fingerprint }) => createCta({ name, status, canonicalCta, ctaFingerprint: fingerprintPayload(fingerprint) }),
    []
  );
  const updateFn = useCallback((id, { name, status }) => updateCtaMetadata(id, { name, status }), []);
  const createVersionFn = useCallback(
    (id, { version, canonicalCta, fingerprint }) => createCtaVersion(id, { version, canonicalCta, ctaFingerprint: fingerprintPayload(fingerprint) }),
    []
  );
  const getIdFn = useCallback((row) => row.cta_id, []);

  const { items, loading, error, mutating, mutationError, clearMutationError, createItem, updateItem, createVersionItem } =
    useEmailCatalogEntityList({
      list: listFn,
      create: createFn,
      update: updateFn,
      createVersion: createVersionFn,
      getId: getIdFn,
    });

  const [expandedId, setExpandedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", status: STATUS_VALUES[0], canonicalCta: "", fingerprint: emptyFingerprint() });
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", status: STATUS_VALUES[0] });
  const [versioningRow, setVersioningRow] = useState(null);
  const [versionForm, setVersionForm] = useState({ version: "", canonicalCta: "", fingerprint: emptyFingerprint() });

  const { offers, psAssets, status: compatibleAssetsStatus } = useCompatibleAssetOptions();

  const openCreate = () => {
    setCreateForm({ name: "", status: STATUS_VALUES[0], canonicalCta: "", fingerprint: emptyFingerprint() });
    clearMutationError();
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    try {
      await createItem({ name: createForm.name.trim(), status: createForm.status, canonicalCta: createForm.canonicalCta, fingerprint: createForm.fingerprint });
      setCreateOpen(false);
    } catch {
      // mutationError already surfaced; keep dialog open
    }
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setEditForm({ name: row.name, status: row.status });
    clearMutationError();
  };

  const submitEdit = async () => {
    try {
      await updateItem(editingRow.cta_id, {
        name: editForm.name.trim() !== editingRow.name ? editForm.name.trim() : undefined,
        status: editForm.status !== editingRow.status ? editForm.status : undefined,
      });
      setEditingRow(null);
    } catch {
      // dialog stays open
    }
  };

  const openVersion = (row) => {
    setVersioningRow(row);
    setVersionForm({ version: "", canonicalCta: row.canonical_cta || "", fingerprint: fingerprintFromResponse(row.cta_fingerprint) });
    clearMutationError();
  };

  const submitVersion = async () => {
    try {
      await createVersionItem(versioningRow.cta_id, {
        version: versionForm.version.trim(),
        canonicalCta: versionForm.canonicalCta,
        fingerprint: versionForm.fingerprint,
      });
      setVersioningRow(null);
    } catch {
      // dialog stays open (e.g. 409 version conflict)
    }
  };

  if (loading) return <CatalogLoadingCard testId="ctas-loading" />;
  if (error) return <CatalogErrorCard message={error} testId="ctas-error" />;

  return (
    <div className="space-y-4" data-testid="ctas-panel">
      <CatalogTable
        testId="ctas-table"
        title="CTAs"
        icon={MousePointerClick}
        headerAction={
          <Button type="button" size="sm" className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500" onClick={openCreate} data-testid="new-ctas-button">
            <Plus className="h-4 w-4" /> New CTA
          </Button>
        }
        rows={items}
        getRowKey={(row) => row.cta_id}
        onRowClick={(row) => setExpandedId(expandedId === row.cta_id ? null : row.cta_id)}
        emptyTitle="No CTAs yet"
        emptySubtitle='Create one with "New CTA" to get started.'
        columns={[
          {
            key: "name",
            label: "CTA",
            render: (row) => (
              <div>
                <div className="text-sm font-medium text-slate-100">{row.name}</div>
                <div className="text-xs text-slate-400">{row.cta_id}</div>
              </div>
            ),
          },
          { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} testId={`ctas-status-${row.cta_id}`} /> },
          { key: "current_version", label: "Current Version", render: (row) => <span className="font-mono text-sm text-slate-300">{row.current_version || "—"}</span> },
        ]}
      />

      {items
        .filter((row) => row.cta_id === expandedId)
        .map((row) => (
          <div key={row.cta_id} className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`ctas-detail-${row.cta_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
                  <Detail label="Governed ID" value={row.cta_id} mono />
                  <Detail label="Status" value={row.status || "—"} />
                  <Detail label="Current Version" value={row.current_version || "—"} mono />
                  <Detail label="Updated" value={row.updated_at ? new Date(row.updated_at).toLocaleString() : "—"} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Canonical CTA (current version)</div>
                  <div className="mt-1 rounded-md border border-white/10 bg-black/20 p-2 text-slate-200">{row.canonical_cta || "—"}</div>
                </div>
                {row.cta_fingerprint && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">CTA Fingerprint</div>
                    <div className="mt-1 grid grid-cols-2 gap-x-6 gap-y-1 rounded-md border border-white/10 bg-black/20 p-2 sm:grid-cols-3" data-testid={`ctas-fingerprint-${row.cta_id}`}>
                      <Detail label="Primary Action" value={row.cta_fingerprint.primary_action || "—"} />
                      <Detail label="Commitment Level" value={row.cta_fingerprint.commitment_level || "—"} />
                      <Detail label="Journey Stage" value={row.cta_fingerprint.customer_journey_stage || "—"} />
                      <Detail label="Compatible Offer" value={row.cta_fingerprint.compatible_offer || "—"} mono />
                      <Detail label="Compatible P.S." value={row.cta_fingerprint.compatible_ps || "—"} mono />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex shrink-0 flex-col gap-2">
                <Button type="button" size="sm" variant="outline" className="gap-1.5 border-white/10 text-slate-200" onClick={() => openEdit(row)} data-testid={`edit-ctas-${row.cta_id}`}>
                  <Pencil className="h-3.5 w-3.5" /> Edit Metadata
                </Button>
                <Button type="button" size="sm" variant="outline" className="gap-1.5 border-violet-400/30 text-violet-200" onClick={() => openVersion(row)} data-testid={`new-version-ctas-${row.cta_id}`}>
                  <History className="h-3.5 w-3.5" /> Create New Version
                </Button>
              </div>
            </div>
          </div>
        ))}

      <EntityFormDialog
        testId="create-ctas-dialog"
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New CTA"
        description="A governed, reusable call-to-action (CTA####), carrying its own CTA Fingerprint (action identity, independent of Creative implementation)."
        onSubmit={submitCreate}
        submitting={mutating}
        error={mutationError}
        submitLabel="Create CTA"
      >
        <div className="space-y-2">
          <Label htmlFor="ctas-name">Name</Label>
          <Input id="ctas-name" value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} required data-testid="ctas-name-input" />
        </div>
        <StatusSelectField id="ctas-status" value={createForm.status} onChange={(status) => setCreateForm((f) => ({ ...f, status }))} values={STATUS_VALUES} testId="ctas-status-select" />
        <div className="space-y-2">
          <Label htmlFor="ctas-content">Canonical CTA</Label>
          <Input id="ctas-content" value={createForm.canonicalCta} onChange={(e) => setCreateForm((f) => ({ ...f, canonicalCta: e.target.value }))} placeholder="e.g. Get My Estimate" required data-testid="ctas-content-input" />
        </div>
        <FingerprintFields
          value={createForm.fingerprint}
          onChange={(fingerprint) => setCreateForm((f) => ({ ...f, fingerprint }))}
          testPrefix="ctas"
          offers={offers}
          psAssets={psAssets}
          optionsStatus={compatibleAssetsStatus}
        />
      </EntityFormDialog>

      {editingRow && (
        <EntityFormDialog
          testId="edit-ctas-dialog"
          open={!!editingRow}
          onOpenChange={(open) => !open && setEditingRow(null)}
          title={`Edit ${editingRow.cta_id}`}
          description='Rename or change the governed status of this CTA. Canonical content and the CTA Fingerprint cannot be changed here -- use "Create New Version" for that.'
          onSubmit={submitEdit}
          submitting={mutating}
          error={mutationError}
          submitLabel="Save Changes"
        >
          <div className="space-y-2">
            <Label htmlFor="ctas-edit-name">Name</Label>
            <Input id="ctas-edit-name" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} required data-testid="ctas-edit-name-input" />
          </div>
          <StatusSelectField id="ctas-edit-status" value={editForm.status} onChange={(status) => setEditForm((f) => ({ ...f, status }))} values={STATUS_VALUES} testId="ctas-edit-status-select" />
        </EntityFormDialog>
      )}

      {versioningRow && (
        <EntityFormDialog
          testId="new-version-ctas-dialog"
          open={!!versioningRow}
          onOpenChange={(open) => !open && setVersioningRow(null)}
          title={`New Version — ${versioningRow.cta_id}`}
          description="Creates a new, immutable Version. The current Version stays historically accessible and unchanged."
          onSubmit={submitVersion}
          submitting={mutating}
          error={mutationError}
          submitLabel="Create New Version"
        >
          <div className="rounded-md border border-white/10 bg-black/20 p-3 text-sm">
            <div className="flex justify-between text-slate-400">
              <span>Asset</span>
              <span className="font-mono text-slate-200" data-testid="new-version-ctas-asset-id">{versioningRow.cta_id}</span>
            </div>
            <div className="mt-1 flex justify-between text-slate-400">
              <span>Current version</span>
              <span className="font-mono text-slate-200" data-testid="new-version-ctas-current">{versioningRow.current_version || "—"}</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ctas-new-version">New Version</Label>
            <Input id="ctas-new-version" value={versionForm.version} onChange={(e) => setVersionForm((f) => ({ ...f, version: e.target.value }))} placeholder="e.g. 2.0.0" required data-testid="ctas-new-version-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ctas-new-content">New Canonical CTA</Label>
            <Input id="ctas-new-content" value={versionForm.canonicalCta} onChange={(e) => setVersionForm((f) => ({ ...f, canonicalCta: e.target.value }))} required data-testid="ctas-new-content-input" />
          </div>
          <FingerprintFields
            value={versionForm.fingerprint}
            onChange={(fingerprint) => setVersionForm((f) => ({ ...f, fingerprint }))}
            testPrefix="ctas-new"
            offers={offers}
            psAssets={psAssets}
            optionsStatus={compatibleAssetsStatus}
          />
        </EntityFormDialog>
      )}
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={mono ? "font-mono text-slate-200" : "text-slate-200"}>{value}</div>
    </div>
  );
}
